import { useState } from 'react'
import {
  dashboardPeriodDays,
  type DashboardPeriod,
  type DashboardStats,
  type MessageTrend,
} from '@/services/dashboardApi'
import { useDashboardStats } from '@/hooks/useDashboardStats'
import { useMessageTrends } from '@/hooks/useMessageTrends'
import { useChannelInstances } from '@/hooks/useChannelInstances'
import { useAuthStore } from '@/store/authStore'
import SetupChecklist from '@/components/dashboard/SetupChecklist'
import WarrantyCoverageAlert from '@/components/dashboard/WarrantyCoverageAlert'
import { AttentionCenter } from '@/components/dashboard/AttentionCenter'
import { DashboardSummaryPanel } from '@/components/dashboard/DashboardSummaryPanel'
import { DashboardAnalysisPanel } from '@/components/dashboard/DashboardAnalysisPanel'
import { DashboardHeader } from '@/components/dashboard/DashboardHeader'
import { DashboardSkeleton } from '@/components/dashboard/DashboardSkeleton'
import { DashboardTabs } from '@/components/dashboard/DashboardTabs'
import {
  readDashboardTab,
  saveDashboardTab,
  type DashboardTab,
} from '@/components/dashboard/dashboardTabState'
import { computeQualityMetrics, type QualityMetrics } from '@/components/dashboard/dashboardMetrics'

export interface DashboardContentViewProps {
  stats: DashboardStats
  hasAnyChannel: boolean
  displayName: string
  loading: boolean
  metrics: QualityMetrics
  period?: DashboardPeriod
  onPeriodChange?: (period: DashboardPeriod) => void
  trendPoints?: MessageTrend[]
  trendLoading?: boolean
  activeTab?: DashboardTab
  onTabChange?: (tab: DashboardTab) => void
}

const noop = () => {}

/**
 * Vista presentacional del dashboard: separada del contenedor para que
 * las pruebas de render puedan fijar qué secciones y valores se muestran.
 */
export function DashboardContentView({
  stats,
  hasAnyChannel,
  displayName,
  loading,
  metrics,
  period = 'today',
  onPeriodChange,
  trendPoints,
  trendLoading,
  activeTab = 'summary',
  onTabChange = noop,
}: DashboardContentViewProps) {
  return (
    <div data-tour="dashboard-main" className="space-y-4 pb-6">
      <DashboardHeader
        displayName={displayName}
        unreadCount={stats.overview.conversationsUnread}
        period={period}
        onPeriodChange={onPeriodChange}
      />

      <DashboardTabs activeTab={activeTab} onChange={onTabChange} />

      {activeTab === 'summary' ? (
        <DashboardSummaryPanel
          stats={stats}
          hasAnyChannel={hasAnyChannel}
          metrics={metrics}
          period={period}
          trendPoints={trendPoints}
          trendLoading={trendLoading}
        />
      ) : (
        <DashboardAnalysisPanel stats={stats} loading={loading} />
      )}
    </div>
  )
}

export default function DashboardPage() {
  const [period, setPeriod] = useState<DashboardPeriod>('today')
  const tenantId = useAuthStore((state) => state.tenantId)
  const [activeTab, setActiveTab] = useState<DashboardTab>(() => readDashboardTab(tenantId))
  const { data: stats, isPending, isError, refetch } = useDashboardStats(period)
  const { data: trends, isPending: trendsPending } = useMessageTrends(dashboardPeriodDays(period))
  const { data: instances, isPending: channelsPending } = useChannelInstances()
  const user = useAuthStore((state) => state.user)
  const displayName = user?.name || user?.email?.split('@')[0] || 'Usuario'
  // Mientras cargan los canales no se anuncia "sin canales": evita un falso aviso.
  const hasAnyChannel = channelsPending || (instances?.length ?? 0) > 0

  const handleTabChange = (tab: DashboardTab) => {
    setActiveTab(tab)
    saveDashboardTab(tenantId, tab)
  }

  if (isPending && !stats) return <DashboardSkeleton />

  if (!stats) {
    return (
      <div className="space-y-4">
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4"
        >
          <p className="text-sm font-medium text-rose-800">No pudimos cargar las estadísticas del período.</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-sm font-semibold text-rose-800 hover:bg-rose-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
          >
            Reintentar
          </button>
        </div>
        {/* Lo accionable que no depende de /stats sigue visible aunque falle. */}
        <AttentionCenter hasAnyChannel={hasAnyChannel} />
        <WarrantyCoverageAlert />
        <SetupChecklist />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {isError && (
        <div
          role="status"
          className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          No pudimos actualizar las estadísticas; mostramos los últimos datos disponibles.
        </div>
      )}
      <DashboardContentView
        stats={stats}
        hasAnyChannel={hasAnyChannel}
        displayName={displayName}
        loading={isPending}
        metrics={computeQualityMetrics(stats.overview)}
        period={period}
        onPeriodChange={setPeriod}
        trendPoints={trends}
        trendLoading={trendsPending}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />
    </div>
  )
}
